/* eslint-disable no-unused-vars */
import SignClient from '@walletconnect/sign-client'
import {SessionTypes} from '@walletconnect/types'
import {Method, NetworkType} from '@/libs/wallet-connect-sdk-core/types'
import {WalletPlatform} from '@/enums/WalletPlatform'
import {
  delay,
  openWalletForWalletConnectSession,
} from '@/helpers/WalletConnectMobileHelper'
import {isMobileBrowser} from '@/helpers/MobileBrowserHelper'
import {ConnectionStrategy} from './ConnectionStrategy'
import {WalletConnectProviderContext} from './WalletConnectContext'

let signClientPromise: Promise<SignClient> | null = null

const resolveWalletConnectMetadataUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }

  return 'https://ndapp.org/'
}

export class WalletConnectConnection implements ConnectionStrategy {
  private signClient: SignClient | null = null
  private session: SessionTypes.Struct | null = null
  private onDeleteAdapter?: () => void
  private listenersBound = false

  constructor(
    private readonly networkProvider: () => NetworkType,
    private readonly methods: Method[],
    private readonly walletImplementation: 'n3' | 'neoX',
    private readonly walletPlatform: WalletPlatform
  ) {}

  private get network(): NetworkType {
    return this.networkProvider()
  }

  async init(onDeleteAdapter?: () => void) {
    this.onDeleteAdapter = onDeleteAdapter

    if (!signClientPromise) {
      signClientPromise = SignClient.init({
        projectId: '8b536aaae30c4232e8f4cf3f4617499d',
        relayUrl: 'wss://relay.walletconnect.com',
        metadata: {
          name: 'nDapp',
          description:
            'Your source of information and data for applications and development groups within the Neo blockchain ecosystem.',
          url: resolveWalletConnectMetadataUrl(),
          icons: ['https://ndapp.org/dist/img/ndapp-loader.png'],
        },
      }).catch(error => {
        signClientPromise = null
        throw error
      })
    }

    this.signClient = await signClientPromise

    if (!this.listenersBound) {
      this.listenersBound = true

      this.signClient.on('session_delete', async event => {
        if (this.session?.topic !== event.topic) {
          return
        }

        this.session = null
        this.onDeleteAdapter?.()
      })
    }

    const namespace = this.network.split(':')[0]

    for (const session of this.signClient.session.values) {
      if (session.namespaces[namespace]) {
        const account = session.namespaces[namespace]?.accounts?.[0]
        const sessionChain = account
          ?.split(':')
          .slice(0, 2)
          .join(':')
        if (sessionChain && sessionChain !== this.network) {
          console.info('[WalletConnect] skipping session on another chain', {
            wallet: this.walletPlatform,
            sessionChain,
            chainInUse: this.network,
          })
          continue
        }

        this.session = session
        break
      }
    }
  }

  isInstalled() {
    return true
  }

  async connect() {
    if (!this.signClient) throw new Error('Sign client not initialized')
    if (this.methods.length === 0)
      throw new Error('At least one method is required')

    const namespace = this.network.split(':')[0]
    const requiredNamespaces = {
      [namespace]: {
        chains: [this.network],
        methods: this.methods,
        events: [],
      },
    }

    console.info('[WalletConnect] requesting session', {
      wallet: this.walletPlatform,
      walletImplementation: this.walletImplementation,
      chain: this.network,
      requiredNamespaces,
    })

    const {approval, uri} = await this.signClient.connect({requiredNamespaces})

    const approvalWrapper = async () => {
      try {
        this.session = await approval()

        console.info('[WalletConnect] session approved', {
          chain: this.network,
          accounts: this.session?.namespaces?.[namespace]?.accounts,
        })
      } catch (error) {
        console.error('[WalletConnect] session refused', {
          wallet: this.walletPlatform,
          chainRequested: this.network,
          code: (error as {code?: unknown} | null)?.code,
          message: (error as {message?: unknown} | null)?.message,
          error,
        })

        throw error
      }
    }

    return {
      approval: approvalWrapper,
      uri: uri ?? null,
    }
  }

  getConnectedProvider(): WalletConnectProviderContext | null {
    if (!this.signClient || !this.session) {
      return null
    }

    return {
      type: 'walletconnect',
      session: this.session,
      signClient: this.signClient,
      network: this.network,
      walletImplementation: this.walletImplementation,
      walletPlatform: this.walletPlatform,
    }
  }

  async disconnect() {
    if (!this.signClient || !this.session) {
      return
    }

    await this.signClient.disconnect({
      topic: this.session.topic,
      reason: {
        code: 5900,
        message: 'USER_DISCONNECTED',
      },
    })

    this.session = null
  }
}

export const getWalletConnectAddress = (
  provider: WalletConnectProviderContext
): string | null => {
  const namespace = provider.network.split(':')[0]
  const account = provider.session.namespaces[namespace]?.accounts[0]

  if (!account) {
    return null
  }

  return account.split(':')[2]
}

export const sendWalletConnectRequest = async (
  provider: WalletConnectProviderContext,
  request: Record<string, unknown>
): Promise<string> => {
  openWalletForWalletConnectSession(provider.session, provider.walletPlatform)

  if (isMobileBrowser()) {
    await delay(600)
  }

  return provider.signClient.request({
    topic: provider.session.topic,
    chainId: provider.network,
    request: request as {method: string; params: unknown},
  })
}

export const resolveWalletConnectInvoke = async (
  provider: WalletConnectProviderContext,
  payload: Record<string, unknown>
): Promise<string> => {
  if (provider.walletImplementation === 'n3') {
    const request = {
      id: 1,
      jsonrpc: '2.0',
      method: 'invokeFunction',
      params: payload,
    }

    return sendWalletConnectRequest(provider, request)
  }

  const request = {
    method: 'eth_sendTransaction',
    params: [payload],
  }

  return sendWalletConnectRequest(provider, request)
}
